// Floor-plane geometry helpers for snap-to-wall + collision detection.
//
// Coordinate convention (three.js, y up):
//   - room walls live in the XZ plane
//   - a cabinet's local frame: x∈[0,W], y∈[0,H], z∈[0,D]
//     local (0,0,0) = bottom-back-left exterior corner
//     back face at z=0, front face at z=D
//   - cabinet `position` is the world location of its local (0,0,0)
//   - cabinet `rotation` is rotation around Y (radians)

import type { Cabinet, Id, Wall } from "@/lib/types";

/** Snap distance (inches) — cabinets within this of a wall back-face-flush
 *  position get snapped. Empirically tuned: 6" is just barely > the depth of
 *  a wall cabinet (12") / base cabinet (24") so dragging-by-half doesn't grab. */
export const SNAP_DIST = 6;

/** Rotation tolerance for axis-aligned snap (radians). 30°. */
export const SNAP_ROT = Math.PI / 6;

// ── snapToWall ───────────────────────────────────────────────────

interface SnapResult {
  position: [number, number, number];
  rotation: number;
  wallIndex: number;
}

/**
 * If a cabinet's back face is within SNAP_DIST of a wall AND its rotation
 * is within SNAP_ROT of the wall-perpendicular target, return a snapped
 * (position, rotation). Otherwise return null.
 *
 * Only handles axis-aligned walls (90° corners) for now — that's >95% of
 * real-world rooms. Curved / angled walls land in Phase 7 when we have a
 * proper room-editor.
 */
export function snapToWall(
  pos: [number, number, number],
  rotation: number,
  dims: { w: number; h: number; d: number },
  walls: Wall[],
): SnapResult | null {
  const W = dims.w, D = dims.d;

  // Cabinet back-face midpoint in world coords (y is irrelevant for floor snap).
  const c = Math.cos(rotation), s = Math.sin(rotation);
  const backX = pos[0] + (W / 2) * c;
  const backZ = pos[2] - (W / 2) * s;

  let best: { dist: number; snap: SnapResult } | null = null;

  for (let i = 0; i < walls.length; i++) {
    const wall = walls[i]!;
    const target = wallSnapTarget(wall, backX, backZ, W, D, rotation);
    if (!target) continue;
    if (target.distance > SNAP_DIST) continue;
    if (Math.abs(angleDelta(rotation, target.rotation)) > SNAP_ROT) continue;

    // Re-compute cabinet origin position so back-face midpoint sits exactly
    // at target.backCenter, with the new rotation.
    const c2 = Math.cos(target.rotation), s2 = Math.sin(target.rotation);
    const newX = target.backCenter[0] - (W / 2) * c2;
    const newZ = target.backCenter[1] + (W / 2) * s2;

    const candidate: SnapResult = {
      position: [newX, pos[1], newZ],
      rotation: target.rotation,
      wallIndex: i,
    };
    if (!best || target.distance < best.dist) {
      best = { dist: target.distance, snap: candidate };
    }
  }

  return best?.snap ?? null;
}

interface WallTarget {
  /** [worldX, worldZ] where the back-face midpoint should sit after snap. */
  backCenter: [number, number];
  /** Rotation that puts cabinet's back face flush against wall, doors facing room. */
  rotation: number;
  /** Distance from current back-face midpoint to target. */
  distance: number;
}

/**
 * For an axis-aligned wall segment, compute the snap target.
 * Returns null if cabinet wouldn't fit along this wall (too far past either end).
 */
function wallSnapTarget(
  wall: Wall,
  backX: number,
  backZ: number,
  cabW: number,
  _cabD: number,
  _curRotation: number,
): WallTarget | null {
  const [ax, az] = wall.a;
  const [bx, bz] = wall.b;
  const dx = bx - ax;
  const dz = bz - az;
  const len = Math.hypot(dx, dz);
  if (len < 1e-3) return null;

  // Wall direction (unit) + perpendicular (interior normal: left-hand of CCW winding).
  const ux = dx / len, uz = dz / len;
  // For a CCW-wound rectangular room (south x+, east z+, north x-, west z-)
  // in three.js's right-handed XZ plane viewed from +Y, the inward normal is
  // a 90° CCW rotation of the wall direction: (-uz, ux). Trace it: south wall
  // (1,0) → (0,1) = +Z (room is at z>0 from south wall). Correct.
  const nx = -uz, nz = ux;

  // Parametric position along the wall axis where the cabinet center projects.
  const tx = backX - ax;
  const tz = backZ - az;
  const along = tx * ux + tz * uz;        // along the wall
  const perp  = tx * nx + tz * nz;        // perpendicular into room

  // Reject if the cabinet would overhang either wall end (with W/2 margin).
  if (along - cabW / 2 < -SNAP_DIST) return null;
  if (along + cabW / 2 > len + SNAP_DIST) return null;
  // Reject if cabinet is on the outside of the wall (perp < 0 means we're past
  // the wall into the wall solid — bad).
  if (perp < -1) return null;

  // Reject if the cabinet would land inside an opening (no wall there to
  // mount against). We check whether the cabinet's along-range overlaps any
  // opening with > 50% of the cabinet width — partial overlaps with a small
  // opening edge are tolerated.
  const cabStart = along - cabW / 2;
  const cabEnd   = along + cabW / 2;
  for (const op of (wall.openings ?? [])) {
    const opStart = op.startAlong;
    const opEnd   = op.startAlong + op.length;
    const overlap = Math.max(0, Math.min(cabEnd, opEnd) - Math.max(cabStart, opStart));
    if (overlap > cabW / 2) return null;
  }

  // Target back-face center: project onto wall axis (clamped to its length) +
  // sit flush against the wall (perp = wall.thickness/2 + ε).
  const clampedAlong = Math.max(cabW / 2, Math.min(len - cabW / 2, along));
  const flushPerp    = wall.thickness / 2;
  const targetX      = ax + clampedAlong * ux + flushPerp * nx;
  const targetZ      = az + clampedAlong * uz + flushPerp * nz;

  // Target rotation: back face normal should equal -inward-normal (so doors
  // face the room). The cabinet's back face normal in world is
  // (-sin(rot), 0, -cos(rot)) since local back-normal = (0,0,-1).
  // We need (-sin, -cos) = (-nx, -nz) → sin(rot) = nx, cos(rot) = nz.
  const rotation = Math.atan2(nx, nz);

  // Distance from current backCenter to target.
  const distance = Math.hypot(backX - targetX, backZ - targetZ);

  return {
    backCenter: [targetX, targetZ],
    rotation,
    distance,
  };
}

/** Shortest signed angle from `a` to `b` in radians, normalised to (-π, π]. */
function angleDelta(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= 2 * Math.PI;
  if (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

// ── Collision detection (rotated AABB on floor) ──────────────────

interface RotatedRect {
  /** 4 corners on the XZ plane, world coords. */
  corners: Array<[number, number]>;
  /** Y-range so wall vs floor cabinets don't collide. */
  yMin: number;
  yMax: number;
}

function cabinetRect(c: Cabinet): RotatedRect {
  const W = c.width, H = c.height, D = c.depth;
  const cs = Math.cos(c.rotation || 0), sn = Math.sin(c.rotation || 0);
  const x = c.position[0], z = c.position[2];
  const y = c.position[1] || 0;

  // Local corners (z=0 is back, z=D is front).
  const local: Array<[number, number]> = [
    [0, 0],
    [W, 0],
    [W, D],
    [0, D],
  ];
  const corners = local.map(([lx, lz]): [number, number] => [
    x + lx * cs + lz * sn,
    z - lx * sn + lz * cs,
  ]);
  return { corners, yMin: y, yMax: y + H };
}

/** SAT-based intersection test for two rotated rectangles on XZ plane.
 *  Returns true if the two rects overlap with at least `tol` inches penetration. */
function rectsOverlap(a: RotatedRect, b: RotatedRect, tol = 0.05): boolean {
  // Y-range check first (cheap).
  if (a.yMax <= b.yMin + tol || b.yMax <= a.yMin + tol) return false;

  // Two unique axes per rect (edges 0→1 and 1→2 give perpendicular directions).
  const axes: Array<[number, number]> = [];
  for (const r of [a, b]) {
    for (let i = 0; i < 2; i++) {
      const p0 = r.corners[i]!;
      const p1 = r.corners[i + 1]!;
      const dx = p1[0] - p0[0];
      const dz = p1[1] - p0[1];
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      axes.push([dx / len, dz / len]);
    }
  }
  for (const [ax, az] of axes) {
    let aMin = Infinity, aMax = -Infinity;
    let bMin = Infinity, bMax = -Infinity;
    for (const [cx, cz] of a.corners) {
      const p = cx * ax + cz * az;
      if (p < aMin) aMin = p;
      if (p > aMax) aMax = p;
    }
    for (const [cx, cz] of b.corners) {
      const p = cx * ax + cz * az;
      if (p < bMin) bMin = p;
      if (p > bMax) bMax = p;
    }
    if (aMax <= bMin + tol || bMax <= aMin + tol) return false; // separating axis found
  }
  return true;
}

// ── Wall vertex graph ────────────────────────────────────────────

export interface Vertex {
  /** Stable id derived from initial position; survives small drags. */
  id: string;
  x: number;
  z: number;
  /** Indices of walls that touch this vertex (as either end). */
  walls: Array<{ wallIndex: number; end: "a" | "b" }>;
}

/** Snap two endpoints onto the same vertex if they're within `tol` inches. */
export function buildVertexGraph(walls: Wall[], tol = 0.5): Vertex[] {
  const verts: Vertex[] = [];
  function key(x: number, z: number): number {
    for (let i = 0; i < verts.length; i++) {
      const v = verts[i]!;
      if (Math.hypot(v.x - x, v.z - z) <= tol) return i;
    }
    verts.push({ id: `v${verts.length}_${Math.round(x)}_${Math.round(z)}`, x, z, walls: [] });
    return verts.length - 1;
  }
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i]!;
    const ia = key(w.a[0], w.a[1]);
    const ib = key(w.b[0], w.b[1]);
    verts[ia]!.walls.push({ wallIndex: i, end: "a" });
    verts[ib]!.walls.push({ wallIndex: i, end: "b" });
  }
  return verts;
}

/** Apply a vertex move to all walls touching that vertex.  Returns new walls. */
export function moveVertex(
  walls: Wall[],
  vertex: Vertex,
  nx: number,
  nz: number,
): Wall[] {
  const out = walls.map((w) => ({ ...w, a: [...w.a] as [number, number], b: [...w.b] as [number, number] }));
  for (const ref of vertex.walls) {
    const w = out[ref.wallIndex]!;
    if (ref.end === "a") { w.a[0] = nx; w.a[1] = nz; }
    else                  { w.b[0] = nx; w.b[1] = nz; }
  }
  return out;
}

// ── Room polygon + point-in-room ─────────────────────────────────

/** Build the room's bounding polygon by walking the vertex graph.
 *  Returns an ordered list of [x,z] corners if the walls form a single
 *  closed loop; otherwise null (open / branching layouts can't be tested).
 *
 *  Used for "is this cabinet inside the room?" — if null, every cabinet is
 *  considered valid (no boundary to check against). */
export function roomPolygon(walls: Wall[]): Array<[number, number]> | null {
  if (walls.length < 3) return null;
  const verts = buildVertexGraph(walls);
  // Each vertex must touch exactly 2 walls for a simple closed polygon.
  for (const v of verts) if (v.walls.length !== 2) return null;
  if (verts.length !== walls.length) return null; // unexpected topology

  const visitedWall = new Set<number>();
  const out: Array<[number, number]> = [];
  let curV = verts[0]!;
  let prevWall = -1;

  while (visitedWall.size < walls.length) {
    out.push([curV.x, curV.z]);
    // Pick the wall touching curV that we haven't visited yet.
    const next = curV.walls.find((r) => r.wallIndex !== prevWall && !visitedWall.has(r.wallIndex));
    if (!next) return null; // dead-end → not a single loop
    visitedWall.add(next.wallIndex);
    prevWall = next.wallIndex;
    const w = walls[next.wallIndex]!;
    const otherEnd: [number, number] = next.end === "a" ? w.b : w.a;
    const nextV = verts.find((v) => Math.abs(v.x - otherEnd[0]) < 0.5 && Math.abs(v.z - otherEnd[1]) < 0.5);
    if (!nextV) return null;
    if (nextV === verts[0] && visitedWall.size === walls.length) break; // closed
    curV = nextV;
  }
  return out.length >= 3 ? out : null;
}

/** Ray-cast point-in-polygon test on the floor plane (XZ). */
export function pointInPolygon(p: [number, number], poly: Array<[number, number]>): boolean {
  const [x, z] = p;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i]!;
    const [xj, zj] = poly[j]!;
    const intersect = (zi > z) !== (zj > z)
      && x < ((xj - xi) * (z - zi)) / (zj - zi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Returns the 4 floor-plane corners of a cabinet in world coords. */
function cabinetFloorCorners(c: Cabinet): Array<[number, number]> {
  const W = c.width, D = c.depth;
  const cs = Math.cos(c.rotation || 0), sn = Math.sin(c.rotation || 0);
  const x = c.position[0], z = c.position[2];
  const local: Array<[number, number]> = [[0, 0], [W, 0], [W, D], [0, D]];
  return local.map(([lx, lz]) => [
    x + lx * cs + lz * sn,
    z - lx * sn + lz * cs,
  ] as [number, number]);
}

/** Returns the set of cabinet ids any corner of which is outside the room
 *  polygon. Catches both fully-outside and partial-overhang cases. */
export function outOfBoundsIds(cabinets: Cabinet[], walls: Wall[]): Set<Id> {
  const poly = roomPolygon(walls);
  if (!poly) return new Set();
  const out = new Set<Id>();
  for (const c of cabinets) {
    const corners = cabinetFloorCorners(c);
    for (const corner of corners) {
      if (!pointInPolygon(corner, poly)) { out.add(c._id); break; }
    }
  }
  return out;
}

/** Segment-segment intersection on a 2D plane.  Endpoints inclusive. */
function segmentsIntersect(
  p: [number, number], q: [number, number],
  r: [number, number], s: [number, number],
): boolean {
  function ccw(a: [number, number], b: [number, number], c: [number, number]): number {
    return (c[1] - a[1]) * (b[0] - a[0]) - (b[1] - a[1]) * (c[0] - a[0]);
  }
  const d1 = ccw(r, s, p);
  const d2 = ccw(r, s, q);
  const d3 = ccw(p, q, r);
  const d4 = ccw(p, q, s);
  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
  // Co-linear endpoint touches are treated as intersection too (good for our
  // "cabinet touching wall" case which is a soft conflict).
  return d1 === 0 || d2 === 0 || d3 === 0 || d4 === 0;
}

/** Expand a wall with openings into its solid floor-segments.  Returns one
 *  entry per solid piece, with absolute (a, b) endpoints and a reference to
 *  the parent wall index. A wall with no openings returns a single entry
 *  spanning its full length. */
export interface SolidSegment {
  a: [number, number];
  b: [number, number];
  thickness: number;
  height: number;
  parentIndex: number;
  /** [start, end] along the parent wall (inches from wall.a). */
  along: [number, number];
}

export function expandWallSegments(walls: Wall[]): SolidSegment[] {
  const out: SolidSegment[] = [];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i]!;
    const ax = w.a[0], az = w.a[1];
    const dx = w.b[0] - ax, dz = w.b[1] - az;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) continue;
    const ux = dx / L, uz = dz / L;

    // Sort openings, clamp, drop overlaps for safety.
    const openings = (w.openings ?? [])
      .filter((o) => o.length > 0 && o.startAlong >= 0 && o.startAlong + o.length <= L)
      .slice()
      .sort((a, b) => a.startAlong - b.startAlong);

    // Walk the wall, emitting solid pieces between openings.
    let cursor = 0;
    for (const op of openings) {
      const opStart = Math.max(cursor, op.startAlong);
      if (opStart > cursor + 1e-3) {
        const sx = ax + cursor * ux, sz = az + cursor * uz;
        const ex = ax + opStart * ux, ez = az + opStart * uz;
        out.push({
          a: [sx, sz], b: [ex, ez],
          thickness: w.thickness, height: w.height,
          parentIndex: i, along: [cursor, opStart],
        });
      }
      cursor = Math.max(cursor, op.startAlong + op.length);
    }
    if (cursor < L - 1e-3) {
      const sx = ax + cursor * ux, sz = az + cursor * uz;
      out.push({
        a: [sx, sz], b: [w.b[0], w.b[1]],
        thickness: w.thickness, height: w.height,
        parentIndex: i, along: [cursor, L],
      });
    }
  }
  return out;
}

/** Full 3D decomposition of walls into renderable box pieces.
 *
 *   - Solid wall portions (between openings) → one piece, full height
 *   - Door opening      → no piece (full-height gap)
 *   - Passage opening   → no piece (full-height gap)
 *   - Window opening    → 2 pieces: sill (floor → sillHeight) + header (headerHeight → wallHeight)
 *   - Door with header  → 1 piece: header (headerHeight → wallHeight) above the door gap
 *
 *  RoomScene consumes this list and renders each piece as an axis-aligned
 *  box rotated around Y. */
export interface WallPiece {
  /** Box center on the XZ plane. */
  cx: number;
  cz: number;
  /** Length along the wall direction. */
  length: number;
  /** Wall thickness perpendicular to direction. */
  thickness: number;
  /** Height of this box. */
  height: number;
  /** Y offset of the box bottom (floor = 0). */
  yMin: number;
  /** Rotation around Y (matches wall direction). */
  rotation: number;
  parentIndex: number;
  /** Discriminator for materials / debugging. */
  kind: "solid" | "header" | "sill";
}

export function expandWallPieces(walls: Wall[]): WallPiece[] {
  const out: WallPiece[] = [];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i]!;
    const ax = w.a[0], az = w.a[1];
    const dx = w.b[0] - ax, dz = w.b[1] - az;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) continue;
    const ux = dx / L, uz = dz / L;
    const rotation = Math.atan2(dz, dx);

    function pushBox(start: number, end: number, yMin: number, height: number, kind: WallPiece["kind"]) {
      const len = end - start;
      if (len < 0.5 || height < 0.5) return;
      const mid = start + len / 2;
      out.push({
        cx: ax + mid * ux,
        cz: az + mid * uz,
        length: len,
        thickness: w.thickness,
        height,
        yMin,
        rotation,
        parentIndex: i,
        kind,
      });
    }

    const openings = (w.openings ?? [])
      .filter((o) => o.length > 0 && o.startAlong >= 0 && o.startAlong + o.length <= L)
      .slice()
      .sort((a, b) => a.startAlong - b.startAlong);

    // Solid pieces between openings (full wall height).
    let cursor = 0;
    for (const op of openings) {
      if (op.startAlong > cursor + 1e-3) {
        pushBox(cursor, op.startAlong, 0, w.height, "solid");
      }
      cursor = Math.max(cursor, op.startAlong + op.length);
    }
    if (cursor < L - 1e-3) {
      pushBox(cursor, L, 0, w.height, "solid");
    }

    // Sill / header above each opening.
    for (const op of openings) {
      const headerY = op.headerHeight ?? (op.type === "window" ? 80 : 84);
      const sillY   = op.sillHeight   ?? (op.type === "window" ? 36 : 0);

      // Sill: floor → sillHeight (window only really; door/passage usually 0).
      if (sillY > 0.5) {
        pushBox(op.startAlong, op.startAlong + op.length, 0, sillY, "sill");
      }
      // Header: headerY → wall.height (almost always present).
      if (w.height - headerY > 0.5) {
        pushBox(op.startAlong, op.startAlong + op.length, headerY, w.height - headerY, "header");
      }
    }
  }
  return out;
}

/** Visual extras associated with openings — window glass panes and door
 *  leaves. Decoupled from `expandWallPieces` (load-bearing wall geometry)
 *  so the simulation/conflict code can ignore decorations. */
export interface WallDecoration {
  kind: "glass" | "door_leaf" | "door_handle" | "mullion" | "jamb";
  cx: number; cy: number; cz: number;        // box center in 3D
  length: number;                            // x (long axis after rotation)
  height: number;                            // y
  thickness: number;                         // z (short axis after rotation)
  rotation: number;                          // around Y
  parentIndex: number;
}

export function expandWallDecorations(walls: Wall[]): WallDecoration[] {
  const out: WallDecoration[] = [];
  for (let i = 0; i < walls.length; i++) {
    const w = walls[i]!;
    const ax = w.a[0], az = w.a[1];
    const dx = w.b[0] - ax, dz = w.b[1] - az;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) continue;
    const ux = dx / L, uz = dz / L;
    const rotation = Math.atan2(dz, dx);
    // Interior normal — CCW winding convention shared with snapToWall.
    const nx = -uz, nz = ux;

    const openings = (w.openings ?? [])
      .filter((o) => o.length > 0 && o.startAlong >= 0 && o.startAlong + o.length <= L);

    for (const op of openings) {
      const midAlong = op.startAlong + op.length / 2;
      const cx0 = ax + midAlong * ux;
      const cz0 = az + midAlong * uz;
      const headerY = op.headerHeight ?? (op.type === "window" ? 80 : 84);
      const sillY   = op.sillHeight   ?? (op.type === "window" ? 36 : 0);

      if (op.type === "window" && headerY > sillY) {
        const glassLen = op.length - 1;
        const glassH   = headerY - sillY - 1;
        const glassCy  = (sillY + headerY) / 2;
        // Glass pane.
        out.push({
          kind: "glass",
          cx: cx0, cz: cz0, cy: glassCy,
          length: glassLen, height: glassH, thickness: 0.5,
          rotation, parentIndex: i,
        });
        // Mullion grid: [rows, cols] divides glass into panes; emit (rows-1)
        // horizontal + (cols-1) vertical mullion bars at the dividers.
        const [rows, cols] = op.panes ?? [1, 1];
        // Vertical mullions
        for (let c = 1; c < cols; c++) {
          const offsetAlong = (op.length / cols) * c - op.length / 2;
          out.push({
            kind: "mullion",
            cx: cx0 + offsetAlong * ux,
            cz: cz0 + offsetAlong * uz,
            cy: glassCy,
            length: 1.5, height: glassH, thickness: 1.5,
            rotation, parentIndex: i,
          });
        }
        // Horizontal mullions (rendered as boxes with length along wall)
        for (let r = 1; r < rows; r++) {
          const yMid = sillY + (glassH * r) / rows;
          out.push({
            kind: "mullion",
            cx: cx0, cz: cz0, cy: yMid,
            length: glassLen, height: 1.5, thickness: 1.5,
            rotation, parentIndex: i,
          });
        }
      } else if (op.type === "door") {
        // Door leaf at hinge corner, rotated by `doorAngle` from the closed
        // (along-wall) position toward the swing side.
        const hingeAtA = (op.hingeSide ?? "a") === "a";
        const swingIn  = (op.swing ?? "in") === "in";
        const psi      = op.doorAngle ?? Math.PI / 2;
        const swingSign = swingIn ? 1 : -1;
        // Angle θ from +wall direction (CCW measured toward interior-normal).
        const theta = hingeAtA
          ? swingSign * psi
          : Math.PI - swingSign * psi;
        const cosT = Math.cos(theta), sinT = Math.sin(theta);
        // Leaf direction in world: cos(θ) along + sin(θ) perp.
        const leafAxisX = cosT * ux + sinT * nx;
        const leafAxisZ = cosT * uz + sinT * nz;
        const hingeAlong = hingeAtA ? op.startAlong : op.startAlong + op.length;
        const hX = ax + hingeAlong * ux;
        const hZ = az + hingeAlong * uz;
        const halfLen = op.length / 2;
        const cx = hX + halfLen * leafAxisX;
        const cz = hZ + halfLen * leafAxisZ;
        const leafRotation = Math.atan2(leafAxisZ, leafAxisX);
        const doorHeight = (headerY - sillY) - 1;
        out.push({
          kind: "door_leaf",
          cx, cz,
          cy: (sillY + headerY) / 2,
          length: op.length - 1.5,
          height: doorHeight,
          thickness: 1.5,
          rotation: leafRotation,
          parentIndex: i,
        });
        // Door knob: small ball ~1″ Ø on the non-hinge edge, ~36″ above floor,
        // on the interior side.
        const knobBaseX = hX + (op.length - 4) * leafAxisX;
        const knobBaseZ = hZ + (op.length - 4) * leafAxisZ;
        out.push({
          kind: "door_handle",
          cx: knobBaseX, cz: knobBaseZ,
          cy: sillY + 36,
          length: 1.5,
          height: 1.5,
          thickness: 1.5,
          rotation: leafRotation,
          parentIndex: i,
        });
        // Door jambs: 2 vertical posts at opening edges (visible casing from
        // either room side). 1″ wide along wall, full wall thickness deep.
        const jambHeight = headerY - sillY;
        const jambCy = sillY + jambHeight / 2;
        for (const along of [op.startAlong, op.startAlong + op.length]) {
          out.push({
            kind: "jamb",
            cx: ax + along * ux,
            cz: az + along * uz,
            cy: jambCy,
            length: 1, height: jambHeight, thickness: w.thickness,
            rotation, parentIndex: i,
          });
        }
      }
    }
  }
  return out;
}

/** Returns cabinets that block a door's 90° swing path. Soft warning —
 *  cabinets in a door's swing arc may not physically overlap any wall but
 *  prevent the door from opening. */
export function doorSwingCollisionIds(cabinets: Cabinet[], walls: Wall[]): Set<Id> {
  const out = new Set<Id>();
  for (let wi = 0; wi < walls.length; wi++) {
    const w = walls[wi]!;
    const ax = w.a[0], az = w.a[1];
    const dx = w.b[0] - ax, dz = w.b[1] - az;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) continue;
    const ux = dx / L, uz = dz / L;
    const nx = -uz, nz = ux;

    const doors = (w.openings ?? []).filter(
      (o) => o.type === "door" && o.length > 0 && o.startAlong + o.length <= L,
    );
    for (const op of doors) {
      const hingeAtA  = (op.hingeSide ?? "a") === "a";
      const swingIn   = (op.swing ?? "in") === "in";
      const hingeSign = hingeAtA ? 1 : -1;
      const swingSign = swingIn ? 1 : -1;
      const radius    = op.length;
      const hingeAlong = hingeAtA ? op.startAlong : op.startAlong + op.length;
      const hX = ax + hingeAlong * ux;
      const hZ = az + hingeAlong * uz;

      for (const c of cabinets) {
        // Test all 4 floor corners + the cabinet center against the swing sector.
        const corners = cabinetFloorCorners(c);
        const cs = Math.cos(c.rotation || 0), sn = Math.sin(c.rotation || 0);
        const center: [number, number] = [
          c.position[0] + (c.width / 2) * cs + (c.depth / 2) * sn,
          c.position[2] - (c.width / 2) * sn + (c.depth / 2) * cs,
        ];
        const probes = [...corners, center];

        let hit = false;
        for (const [px, pz] of probes) {
          const vx = px - hX, vz = pz - hZ;
          const along = (vx * ux + vz * uz) * hingeSign;
          const perp  = (vx * nx + vz * nz) * swingSign;
          if (along < 0 || perp < 0) continue;
          if (along * along + perp * perp > radius * radius) continue;
          hit = true; break;
        }
        if (hit) out.add(c._id);
      }
    }
  }
  return out;
}

/** Returns the set of cabinet ids whose floor footprint overlaps with any
 *  wall solid-segment (cabinet wedged into / crossing a wall). Openings are
 *  excluded — passing through a doorway is fine. */
export function wallConflictIds(cabinets: Cabinet[], walls: Wall[]): Set<Id> {
  if (walls.length === 0) return new Set();
  const segs = expandWallSegments(walls);
  if (segs.length === 0) return new Set();
  const out = new Set<Id>();
  for (const c of cabinets) {
    const corners = cabinetFloorCorners(c);
    for (const seg of segs) {
      let hit = false;
      for (let i = 0; i < 4; i++) {
        const cA = corners[i]!;
        const cB = corners[(i + 1) % 4]!;
        if (segmentsIntersect(cA, cB, seg.a, seg.b)) { hit = true; break; }
      }
      if (hit) { out.add(c._id); break; }
    }
  }
  return out;
}

/** Split a wall into two halves at its midpoint, returning a new walls array.
 *  Creates an implicit new vertex at the split point — caller's vertex graph
 *  rebuild picks it up. */
export function splitWallAtMidpoint(walls: Wall[], idx: number): Wall[] {
  const w = walls[idx];
  if (!w) return walls;
  const mid: [number, number] = [(w.a[0] + w.b[0]) / 2, (w.a[1] + w.b[1]) / 2];
  const w1: Wall = { ...w, b: mid };
  const w2: Wall = { ...w, a: mid };
  return [...walls.slice(0, idx), w1, w2, ...walls.slice(idx + 1)];
}

/** Snap (x, z) onto the nearest existing vertex within `tol` inches.
 *  Returns the snapped coords + the vertex id, or null if no snap. */
export function snapToVertex(
  x: number, z: number,
  vertices: Vertex[],
  tol = 3,
  ignoreId?: string,
): { x: number; z: number; vertexId: string } | null {
  let best: { dist: number; v: Vertex } | null = null;
  for (const v of vertices) {
    if (v.id === ignoreId) continue;
    const d = Math.hypot(v.x - x, v.z - z);
    if (d <= tol && (!best || d < best.dist)) {
      best = { dist: d, v };
    }
  }
  return best ? { x: best.v.x, z: best.v.z, vertexId: best.v.id } : null;
}

/** Snap (bx, bz) onto the nearest 45° axis through (ax, az). */
export function snapToAxis45(
  ax: number, az: number, bx: number, bz: number,
): [number, number] {
  const dx = bx - ax, dz = bz - az;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) return [bx, bz];
  const ang = Math.atan2(dz, dx);
  const snapped = Math.round(ang / (Math.PI / 4)) * (Math.PI / 4);
  return [ax + len * Math.cos(snapped), az + len * Math.sin(snapped)];
}

/** Returns the set of cabinet ids that collide with at least one other. */
export function collisionIds(cabinets: Cabinet[]): Set<Id> {
  const rects = cabinets.map((c) => ({ id: c._id, rect: cabinetRect(c) }));
  const out = new Set<Id>();
  for (let i = 0; i < rects.length; i++) {
    for (let j = i + 1; j < rects.length; j++) {
      if (rectsOverlap(rects[i]!.rect, rects[j]!.rect)) {
        out.add(rects[i]!.id);
        out.add(rects[j]!.id);
      }
    }
  }
  return out;
}
